import { markRaw } from 'vue'
import type { PageState } from '@/types/ainotebook'
import { fillWhite, drawTextInRect } from '@/utils/ainotebook/draw'

export function useExportPdf(opts: {
  pdfDoc: { value: any }
  pages: { value: PageState[] }
  isExporting: { value: boolean }
}) {
  async function downloadPdf() {
    if (!opts.pdfDoc.value || !opts.pages.value.length) return

    opts.isExporting.value = true
    try {
      const { jsPDF } = await import('jspdf')

      const PX_TO_PT = 72 / 96
      let pdf: any = null

      for (let i = 0; i < opts.pages.value.length; i++) {
        const p = opts.pages.value[i]
        const page = markRaw(await opts.pdfDoc.value.getPage(p.pageNumber))

        const scale = 3.0
        const v = page.getViewport({ scale })

        const canvas = document.createElement('canvas')
        canvas.width = Math.floor(v.width)
        canvas.height = Math.floor(v.height)
        const ctx = canvas.getContext('2d')!
        fillWhite(ctx, canvas.width, canvas.height)
        await page.render({ canvasContext: ctx, viewport: v }).promise

        // base for bg sampling
        const baseCanvas = document.createElement('canvas')
        baseCanvas.width = canvas.width
        baseCanvas.height = canvas.height
        const baseCtx = baseCanvas.getContext('2d')!
        baseCtx.drawImage(canvas, 0, 0)

        if (p.analyzed && p.blocks.length) {
          const sx = canvas.width / Math.max(1, p.width)
          const sy = canvas.height / Math.max(1, p.height)

          for (const b of p.blocks) {
            if (!b.isEdited) continue
            drawTextInRect(
              ctx,
              baseCtx,
              b.text,
              { x: b.rect.x * sx, y: b.rect.y * sy, w: b.rect.w * sx, h: b.rect.h * sy },
              b.color,
              b.fontScale
            )
          }
        }

        const img = canvas.toDataURL('image/jpeg', 0.92)

        const pageWmm = (canvas.width * PX_TO_PT) / 2.834645669
        const pageHmm = (canvas.height * PX_TO_PT) / 2.834645669
        const orient = pageWmm > pageHmm ? 'l' : 'p'

        if (!pdf) {
          pdf = new jsPDF({ orientation: orient, unit: 'mm', format: [pageWmm, pageHmm], compress: true })
        } else {
          pdf.addPage([pageWmm, pageHmm], orient)
        }

        const w = pdf.internal.pageSize.getWidth()
        const h = pdf.internal.pageSize.getHeight()
        pdf.addImage(img, 'JPEG', 0, 0, w, h, undefined, 'FAST')
      }

      pdf.save(`ainotebook_${Date.now()}.pdf`)
    } finally {
      opts.isExporting.value = false
    }
  }

  return { downloadPdf }
}
